import { z } from 'zod';
/**
 * Request and response contracts, validated on both sides of every call.
 *
 * ADR-0005 chose an HTTP API over AppSync, which means there is no GraphQL schema
 * acting as the contract. These schemas are that contract instead, and because both
 * the client and the Lambda import them from this package, the two cannot drift.
 */
export const healthResponse = z.object({
    ok: z.literal(true),
    /** Commit the running bundle was built from (NFR-CON-10 attributability). */
    commit: z.string(),
    env: z.enum(['dev', 'prod']),
});
/** Authenticated echo of the caller's identity. Proves the JWT authorizer works end to end. */
export const meResponse = z.object({
    userId: z.string(),
    email: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Sync (docs/design/03-sync-protocol.md §3)
// ---------------------------------------------------------------------------
/** Sync protocol §3: batches of up to 200 events. */
export const PUSH_BATCH_MAX = 200;
/**
 * `POST /sync/push`.
 *
 * `events` is deliberately `unknown[]` rather than an array of parsed events.
 * Validating them here would make one malformed event reject the whole request,
 * and §3 step 1 promises the opposite: "one bad event rejects that event only,
 * not the batch." The handler runs `partitionEvents` instead.
 *
 * Ascending clock order is what the client sends, but the server does not require
 * it: the merge is order-independent, so rejecting a batch over ordering would be
 * a failure mode with no benefit.
 */
export const pushRequest = z.object({
    deviceId: z.string().min(1).max(64),
    events: z.array(z.unknown()).max(PUSH_BATCH_MAX),
});
/** A field write that was logged but lost the merge, for the client to surface (NFR-REL-5). */
export const supersededField = z.object({
    eventId: z.string(),
    itemId: z.string(),
    field: z.string(),
    yourValue: z.unknown(),
    winningValue: z.unknown(),
    winningHlc: z.string(),
});
/**
 * An event the schema refused.
 *
 * Not in §3's response example, and it has to be: without it a permanently
 * invalid event is retried forever, because the client only clears its outbox on
 * `accepted` or `duplicates` (§4, "Pushing"). One malformed event would wedge the
 * outbox and every later event behind it. Reported so the client can drop it and
 * tell the user, rather than retry until the end of time.
 */
export const rejectedEvent = z.object({
    index: z.int(),
    eventId: z.string().optional(),
    message: z.string(),
});
export const pushResponse = z.object({
    /** Event ids durably stored. */
    accepted: z.array(z.string()),
    /** Already seen; the client clears these too. */
    duplicates: z.array(z.string()),
    /** Applied to the log, lost the per-field merge. */
    superseded: z.array(supersededField),
    /** Refused by the schema and never written. The client must stop retrying these. */
    rejected: z.array(rejectedEvent),
    /** The server's clock after accepting this batch. */
    serverHlc: z.string(),
});
/** Sync protocol §3: `GET /sync/pull?since=<hlc>&limit=500`. */
export const PULL_LIMIT_MAX = 500;
export const PULL_LIMIT_DEFAULT = 500;
export const pullQuery = z.object({
    /**
     * Exclusive cursor: a log sort key, `<hlc>#<eventId>`.
     *
     * A bare clock also works and is what bootstrap hands back, since
     * `<hlc>#<eventId>` sorts after `<hlc>` -- so an event at exactly that clock is
     * re-delivered rather than skipped. Re-delivery is harmless because the merge
     * is idempotent; skipping would be data loss, so the comparison is deliberately
     * biased that way.
     */
    since: z.string().optional(),
    limit: z.int().min(1).max(PULL_LIMIT_MAX).default(PULL_LIMIT_DEFAULT),
});
export const pullResponse = z.object({
    /** Ascending by clock, which is the order the log is stored in. */
    events: z.array(z.unknown()),
    /** Pass as `since` on the next pull. Unchanged from the request when nothing was returned. */
    nextCursor: z.string(),
    hasMore: z.boolean(),
});
/**
 * `GET /sync/bootstrap`.
 *
 * Current state for a device signing in for the first time or recovering from an
 * eviction, so it need not replay the whole log. Paginated because the state
 * partition runs to tens of thousands of rows at the reference workload, well past
 * what one response can carry.
 *
 * Records mirror the stored row shapes, which is what the client replica mirrors
 * too (data model §6). That couples the wire format to the storage layout, and is
 * accepted for v1 on the grounds that there is exactly one client and it is
 * deployed from this repository.
 */
export const bootstrapQuery = z.object({
    /** Opaque continuation from a previous page. Carries the cursor, so it cannot drift. */
    page: z.string().optional(),
    limit: z.int().min(1).max(1000).default(500),
});
export const bootstrapResponse = z.object({
    /** Tagged by `kind`; the client dispatches on it when loading its replica. */
    records: z.array(z.looseObject({ kind: z.string() })),
    /** Absent on the last page. */
    nextPage: z.string().optional(),
    /**
     * The pull cursor to start from once the whole bootstrap has been consumed.
     *
     * Captured from the log *before* the first page of state is read, never after.
     * Taken afterwards, an event written during the read would already be in the
     * state yet sit below the cursor, and the client would never pull it -- silent
     * data loss. Taken before, such an event is merely delivered twice, which the
     * merge absorbs.
     */
    cursor: z.string(),
});
// ---------------------------------------------------------------------------
// Photos (docs/design/03-sync-protocol.md §3, ADR-0007)
// ---------------------------------------------------------------------------
/** NFR-SEC-4: a credential scoped to the owner, expiring within the hour. */
export const PRESIGN_TTL_SECONDS = 3600;
/**
 * S3 object keys for photos.
 *
 * Here rather than only on the server because the client has to derive them
 * offline. A photo captured in a basement is written to the local replica with a
 * `photo.added` event straight away (FR-10.1), and that event carries its keys --
 * so if the keys could only come from a presign call, capture would require a
 * network. The server derives the same keys from the validated claim when it
 * signs, and refuses any key outside the caller's prefixes, so computing them
 * locally grants nothing.
 */
export const photoObjectKey = {
    original: (userId, photoId) => `originals/${userId}/${photoId}`,
    derivative: (userId, photoId) => `derived/${userId}/${photoId}`,
};
/**
 * Upload size ceilings, enforced by the signature rather than by the client.
 *
 * A presigned PUT accepts a body of any size: the signature covers the key and
 * the content type, not the length. So a bug or a tampered client could push
 * gigabytes into the bucket, which is a direct cost exposure against NFR-CON-4's
 * $25 ceiling -- and the budget alarm would find out a day later.
 *
 * A presigned POST policy can carry a `content-length-range` condition, which S3
 * enforces at upload time. That is why uploads are POSTs.
 *
 * The derivative ceiling is above its 600 KB target: `encodeWithin` steps quality
 * down to meet the target but returns an oversized blob rather than refusing a
 * stubborn image, and the ceiling must not turn that into a failed upload.
 */
export const MAX_DERIVATIVE_UPLOAD_BYTES = 2 * 1024 * 1024;
/**
 * Originals are kept at captured resolution for export (FR-11.3), so this is
 * generous: a 48-megapixel phone photo is a few megabytes, and the ceiling exists
 * to stop something absurd rather than to shape what people photograph.
 */
export const MAX_ORIGINAL_UPLOAD_BYTES = 25 * 1024 * 1024;
/** FR-2.3 caps an item at ten photos, so a download batch need not exceed that by much. */
export const PRESIGN_DOWNLOAD_MAX = 100;
/**
 * The content types a photo may be stored as.
 *
 * The derivative is always one of these because the client re-encodes it through
 * a canvas (ADR-0007), which is also what strips EXIF (NFR-SEC-9). Constraining
 * the set matters because the type is signed into the upload URL: anything else
 * uploaded under that signature is rejected by S3 rather than stored and served
 * back later.
 */
export const photoContentType = z.enum(['image/jpeg', 'image/webp', 'image/png']);
/**
 * Fetching a photo the user names by URL (FR-2.17).
 *
 * The browser cannot do this itself for most hosts: a cross-origin `fetch` of an
 * image needs the remote server to opt in with CORS headers, and the sites people
 * copy product photographs from generally do not. So the service fetches it and
 * hands back the bytes.
 *
 * That makes the service fetch a URL a caller supplies, which is a request
 * forgery surface and is treated as one in `photos-fetch.ts`. Nothing about that
 * can be enforced here -- a schema can say the string is a URL, not where it
 * points -- so this contract deliberately carries no allowlist and promises
 * nothing beyond shape.
 */
export const photoFetchRequest = z.object({
    url: z.url().max(2048),
});
/**
 * The bytes, base64 encoded, and what they turned out to be.
 *
 * Base64 because this travels as JSON through API Gateway, and the 33% it costs
 * is paid once on an image already bounded by `MAX_FETCHED_PHOTO_BYTES`. The
 * client decodes it to a `Blob` and hands it to the same `capture` path a file
 * from disk takes, so there is one way photos enter the system.
 */
export const photoFetchResponse = z.object({
    contentType: photoContentType,
    bytes: z.string(),
});
/**
 * The ceiling on a fetched photo.
 *
 * Smaller than `MAX_ORIGINAL_UPLOAD_BYTES`, because this one is spent on the
 * service's own bandwidth and memory rather than on a direct upload to S3, and
 * because a photograph someone found on a web page is not a 48-megapixel
 * original. It is enforced while reading the body, not after.
 */
export const MAX_FETCHED_PHOTO_BYTES = 8 * 1024 * 1024;
/** Enough for a CDN's redirect to a regional host, not enough to be a loop. */
export const MAX_PHOTO_FETCH_REDIRECTS = 3;
export const presignUploadRequest = z.object({
    /** Client-generated, and what makes the resulting key unguessable. */
    photoId: z.uuid(),
    itemId: z.uuid(),
    contentType: photoContentType,
    originalContentType: photoContentType,
});
/**
 * A presigned POST: the form fields S3 requires, and the size it will accept.
 *
 * The client sends `fields` plus the file as `multipart/form-data`. S3 rejects the
 * upload if the body exceeds `maxBytes` or the content type differs, both of which
 * are conditions inside the signed policy rather than promises the client keeps.
 */
export const presignedPost = z.object({
    url: z.string(),
    fields: z.record(z.string(), z.string()),
    key: z.string(),
    maxBytes: z.int(),
});
export const presignUploadResponse = z.object({
    photoId: z.string(),
    /**
     * Derived by the server from the validated claim. The client computes the same
     * keys locally so it can capture offline, and these confirm what the server
     * will actually accept.
     */
    original: presignedPost,
    derivative: presignedPost,
    expiresInSeconds: z.int(),
});
/** Batched, so a scrolling grid costs one round trip rather than one per photo. */
export const presignDownloadRequest = z.object({
    keys: z.array(z.string()).min(1).max(PRESIGN_DOWNLOAD_MAX),
});
export const presignDownloadResponse = z.object({
    /** Keyed by the requested object key. */
    urls: z.record(z.string(), z.string()),
    /**
     * Keys the caller does not own, or that do not exist as photo keys.
     *
     * Reported rather than silently omitted, so a client showing a broken thumbnail
     * can say why instead of retrying forever.
     */
    refused: z.array(z.string()),
    expiresInSeconds: z.int(),
});
export const errorResponse = z.object({
    /** Stable machine-readable code. */
    code: z.string(),
    /** Plain language, safe to show a user: no stack traces or status codes (NFR-UX-8). */
    message: z.string(),
});
// ---------------------------------------------------------------------------
// Web Push reminders (FR-5.12)
// ---------------------------------------------------------------------------
/**
 * How often reminders are sent, or not at all.
 *
 * FR-5.12 requires the cadence to be configurable *or disabled*, so `off` is a
 * value rather than the absence of a subscription. Keeping it as a value means
 * turning reminders off leaves a record saying so, instead of looking identical
 * to never having set them up -- which matters because the browser's own
 * permission grant survives either way.
 */
export const reminderCadence = z.enum(['daily', 'weekly', 'off']);
/**
 * A browser's push subscription, as `pushManager.subscribe` returns it.
 *
 * Deliberately *not* an event in the log. Every other piece of user data syncs
 * to every device because every device needs it; a push endpoint is the opposite
 * -- it addresses one browser, and the others can neither use it nor should hold
 * it. The cadence travels with it for the same reason: "remind me here, this
 * often" is a statement about this device, so silencing a phone should not
 * silence a laptop.
 */
export const pushSubscribeRequest = z.strictObject({
    /** The push service's URL for this browser. Opaque, and treated as such. */
    endpoint: z.string().url().max(2000),
    keys: z.strictObject({
        /** The user agent's public key, base64url. 65 bytes decoded. */
        p256dh: z.string().min(1).max(200),
        /** The authentication secret, base64url. 16 bytes decoded. */
        auth: z.string().min(1).max(100),
    }),
    cadence: reminderCadence,
    /** Which device this is, so re-subscribing replaces rather than accumulates. */
    deviceId: z.string().min(1).max(100),
});
export const pushSubscribeResponse = z.object({
    cadence: reminderCadence,
});
export const pushUnsubscribeRequest = z.strictObject({
    deviceId: z.string().min(1).max(100),
});
/**
 * What a reminder says.
 *
 * Counts, not verdicts. `NFR-SAFE-2` forbids a notification that urges releasing
 * or acquiring anything, so a reminder reports that there is something to look
 * at and never which way to decide -- "3 items are due for review", never
 * "3 items you should get rid of".
 */
export const reminderPayload = z.strictObject({
    title: z.string(),
    body: z.string(),
    /** How many items are due, so the client can badge without parsing prose. */
    due: z.int().min(0),
});
//# sourceMappingURL=contracts.js.map